/* @flow */

import { toast } from 'react-toastify';

import { flattenNotificationMessage, logInfo } from '../debug/debug';
import { Messenger, MessengerEvents } from '../messenger';
import { type NOTIFICATION, type NOTIFICATION_MESSAGE, type NOTIFICATION_SETTINGS, type NOTIFICATION_TYPE, TOAST } from './types';

// Default delay for closing a toast (in ms)
const DEFAULT_CLOSE_DELAY = 5_000;

export default class Notifier {
  static instance: Notifier;

  static isDebugModeEnabled: boolean = false;

  constructor() {
    Messenger.on(MessengerEvents.NOTIFY_ERROR, this.notifyError);
    Messenger.on(MessengerEvents.NOTIFY_INFO, this.notifyInfo);
    Messenger.on(MessengerEvents.NOTIFY_SUCCESS, this.notifySuccess);
    Messenger.on(MessengerEvents.NOTIFY_WARNING, this.notifyWarning);
    Messenger.on(MessengerEvents.NOTIFY_CLOSE, this.notifyClose);
  }

  // $FlowFixMe[unsupported-syntax] Flow does not support symbols yet
  get [Symbol.toStringTag]() {
    return 'Notifier';
  }

  static initialize: (isDebugModeEnabled: boolean) => void = (isDebugModeEnabled) => {
    if (Notifier.instance) {
      return;
    }

    Notifier.isDebugModeEnabled = isDebugModeEnabled;
    Notifier.instance = new Notifier();
  };

  notifyError: (message: NOTIFICATION_MESSAGE, settings?: NOTIFICATION_SETTINGS, ariaLabel?: string) => void = (message, settings, ariaLabel) => {
    this.notify(
      {
        ariaLabel,
        message,
        settings,
      },
      TOAST.Error,
    );
  };

  notifyInfo: (message: NOTIFICATION_MESSAGE, settings?: NOTIFICATION_SETTINGS, ariaLabel?: string) => void = (message, settings, ariaLabel) => {
    this.notify(
      {
        ariaLabel,
        message,
        settings,
      },
      TOAST.Info,
    );
  };

  notifySuccess: (message: NOTIFICATION_MESSAGE, settings?: NOTIFICATION_SETTINGS, ariaLabel?: string) => void = (message, settings, ariaLabel) => {
    this.notify(
      {
        ariaLabel,
        message,
        settings,
      },
      TOAST.Success,
    );
  };

  notifyWarning: (message: NOTIFICATION_MESSAGE, settings?: NOTIFICATION_SETTINGS, ariaLabel?: string) => void = (message, settings, ariaLabel) => {
    this.notify(
      {
        ariaLabel,
        message,
        settings,
      },
      TOAST.Warning,
    );
  };

  notify: (notification: NOTIFICATION, type: NOTIFICATION_TYPE) => void = (notification, type) => {
    const { ariaLabel, message, settings = {} } = notification;

    // QA
    if (Notifier.isDebugModeEnabled && (type === 'warning' || type === 'error')) {
      logInfo(`Notification: ${type} | ${flattenNotificationMessage(notification.message)}`);
    }

    const { toastId } = settings;

    if (toast.isActive(toastId)) {
      // Update existing
      toast.update(toastId, {
        ...settings,
        ariaLabel: ariaLabel ?? message,
        render: message,
        type,
      });
    } else {
      // Create toast
      toast(message, {
        ...settings,
        ariaLabel: ariaLabel ?? message,
        type,
      });
    }
  };

  notifyClose: (toastId: string, delay?: number) => void = (toastId, delay) => {
    setTimeout(() => toast.dismiss(toastId), delay ?? DEFAULT_CLOSE_DELAY);
  };
}
